import type { MemberType } from "./group";

// Currency types for multi-currency support
export type Currency = "JPY" | "USD" | "EUR" | "GBP" | "CNY" | "KRW" | "THB" | "TWD" | "VND";

export interface CurrencyInfo {
  code: Currency;
  symbol: string;
  name: string;
  decimalPlaces: number;
}

export const CURRENCIES: CurrencyInfo[] = [
  { code: "JPY", symbol: "¥", name: "日本円", decimalPlaces: 0 },
  { code: "USD", symbol: "$", name: "USドル", decimalPlaces: 2 },
  { code: "EUR", symbol: "€", name: "ユーロ", decimalPlaces: 2 },
  { code: "GBP", symbol: "£", name: "ポンド", decimalPlaces: 2 },
  { code: "CNY", symbol: "¥", name: "人民元", decimalPlaces: 2 },
  { code: "KRW", symbol: "₩", name: "ウォン", decimalPlaces: 0 },
  { code: "THB", symbol: "฿", name: "バーツ", decimalPlaces: 2 },
  { code: "TWD", symbol: "NT$", name: "台湾ドル", decimalPlaces: 0 },
  { code: "VND", symbol: "₫", name: "ドン", decimalPlaces: 0 },
];

// Rounding unit for settlement amounts
export type RoundingUnit = 1 | 10 | 100 | 1000;

// Expense types for bill splitting
export type ExpenseCategory =
  | "food"
  | "transport"
  | "accommodation"
  | "activity"
  | "shopping"
  | "other";

export interface ExpenseShareResponseDto {
  memberId: string;
  memberType: MemberType;
  memberName: string;
  amount: number;
}

export interface ExpenseResponseDto {
  id: string;
  sessionId: string;
  paidById: string;
  paidByType: MemberType;
  paidByName: string;
  amount: number;
  currency: Currency;
  description: string;
  category: ExpenseCategory;
  date: string;
  shares: ExpenseShareResponseDto[];
  createdAt: string;
  updatedAt: string;
}

export interface MemberBalanceDto {
  memberId: string;
  memberType: MemberType;
  memberName: string;
  totalPaid: number;
  totalOwed: number;
  balance: number;
  avatarEmoji?: string | null;
  avatarColor?: string | null;
}

export interface SettlementDto {
  fromMemberId: string;
  fromMemberType: MemberType;
  fromMemberName: string;
  fromMemberAvatarEmoji?: string | null;
  fromMemberAvatarColor?: string | null;
  toMemberId: string;
  toMemberType: MemberType;
  toMemberName: string;
  toMemberAvatarEmoji?: string | null;
  toMemberAvatarColor?: string | null;
  amount: number;
  currency: Currency;
}

export interface CreateExpenseDto {
  amount: number;
  /** Currency for this expense - defaults to JPY */
  currency?: Currency;
  description: string;
  category: ExpenseCategory;
  date: string;
  shares: { memberId: string; memberType: MemberType; amount: number }[];
  /** Optional payer ID - defaults to current user */
  paidById?: string;
  /** Optional payer type - defaults to current user's type */
  paidByType?: MemberType;
}

export interface UpdateExpenseDto {
  amount?: number;
  /** Currency for this expense */
  currency?: Currency;
  description?: string;
  category?: ExpenseCategory;
  date?: string;
  shares?: { memberId: string; memberType: MemberType; amount: number }[];
  /** Optional payer ID - changes who paid for this expense */
  paidById?: string;
  /** Optional payer type */
  paidByType?: MemberType;
}

// Payment confirmation for mutual settlement confirmation
export type PaymentStatus = "pending" | "paid" | "confirmed";

export interface PaymentConfirmationDto {
  id: string;
  sessionId: string;
  fromMemberId: string;
  fromMemberType: MemberType;
  fromMemberName: string;
  toMemberId: string;
  toMemberType: MemberType;
  toMemberName: string;
  amount: number;
  currency: Currency;
  status: PaymentStatus;
  paidAt: string | null;
  confirmedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePaymentDto {
  fromMemberId: string;
  fromMemberType: MemberType;
  toMemberId: string;
  toMemberType: MemberType;
  amount: number;
  currency: Currency;
}

// Enhanced settlement with payment status
// Note: currency is inherited from SettlementDto
export interface SettlementWithPaymentDto extends SettlementDto {
  paymentId: string | null;
  paymentStatus: PaymentStatus | null;
  paidAt: string | null;
  confirmedAt: string | null;
  toMemberPayPayUrl: string | null; // 受取人のPayPay URL
}

// Balance summary for home dashboard
export interface GroupBalanceDto {
  groupId: string;
  groupName: string;
  balance: number;
  sessionCount: number;
}

export interface BalanceSummaryDto {
  totalReceivable: number;
  totalPayable: number;
  netBalance: number;
  groups: GroupBalanceDto[];
}

// Currency utility functions

/**
 * Get currency info by currency code
 */
export function getCurrencyInfo(currency: Currency): CurrencyInfo {
  return CURRENCIES.find((c) => c.code === currency) ?? CURRENCIES[0];
}

/**
 * Format amount with currency symbol
 */
export function formatCurrency(amount: number, currency: Currency): string {
  const info = getCurrencyInfo(currency);
  const formatted = new Intl.NumberFormat("ja-JP", {
    minimumFractionDigits: info.decimalPlaces,
    maximumFractionDigits: info.decimalPlaces,
  }).format(amount);
  return `${info.symbol}${formatted}`;
}

/**
 * Get currency symbol only
 */
export function getCurrencySymbol(currency: Currency): string {
  return getCurrencyInfo(currency).symbol;
}

/**
 * Get short currency name for display (e.g., "円", "ドル")
 */
export function getCurrencyShortName(currency: Currency): string {
  const shortNames: Record<Currency, string> = {
    JPY: "円",
    USD: "ドル",
    EUR: "ユーロ",
    GBP: "ポンド",
    CNY: "元",
    KRW: "ウォン",
    THB: "バーツ",
    TWD: "台湾ドル",
    VND: "ドン",
  };
  return shortNames[currency] ?? currency;
}
